import React from "react";
import { motion } from "framer-motion";
import { LOGOS } from "../data/content";
import { useBooking } from "./BookingModal";

const stats = [
  { n: "20+", label: "Branches nationwide" },
  { n: "50k+", label: "Students mentored" },
  { n: "IAO", label: "Accredited institution" },
  { n: "15+", label: "Years in beauty" },
  { n: "100%", label: "Pro-grade products" },
  { n: "6", label: "Make-up brand options" },
];

const Intro = () => {
  const { openBooking } = useBooking();
  return (
    <section
      id="intro"
      data-testid="intro-section"
      className="relative px-6 py-32 md:px-12 md:py-40"
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="grid grid-cols-1 gap-14 md:grid-cols-12 md:gap-20">
          {/* Left — copy */}
          <div className="md:col-span-7">
            <div className="chip mb-6">
              <span className="dot" />
              01 · About 99
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.7, 0, 0.2, 1] }}
              className="font-serif text-5xl leading-[0.9] tracking-tight md:text-7xl lg:text-8xl"
            >
              One name.
              <br />
              <span className="italic-serif text-[color:var(--primary)]">
                Twenty plus
              </span>{" "}
              addresses.
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="mt-10 space-y-6 text-base leading-relaxed text-[color:var(--muted)] md:text-lg"
            >
              <p>
                <span className="text-[color:var(--text)]">
                  99 Institute of Beauty &amp; Wellness
                </span>{" "}
                is a beauty &amp; wellness house with{" "}
                <span className="text-[color:var(--primary)]">
                  20+ branches
                </span>{" "}
                across the country — a full-service salon and a formally
                trained academy under one signature.
              </p>
              <p>
                This is our{" "}
                <span className="italic-serif text-[color:var(--text)]">
                  Patiala flagship
                </span>{" "}
                — near Fountain Chowk, back side of Bhagat Petrol Pump.
                Everything you see below — hair, skin, makeup, nails, body,
                bridal, plus the entire academy curriculum — happens on this
                floor.
              </p>
              <p>
                Our academy is{" "}
                <span className="text-[color:var(--text)]">
                  accredited by the International Accreditation Organization
                  (IAO)
                </span>{" "}
                — every certificate, diploma and long-term program you earn
                here is recognised as an international qualification.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.3 }}
              className="mt-12 flex flex-col gap-4 sm:flex-row sm:items-center"
            >
              <button
                data-testid="intro-book-btn"
                onClick={openBooking}
                className="inline-flex items-center gap-3 border border-[color:var(--primary)] bg-[color:var(--primary)]/10 px-6 py-4 font-mono text-[10px] uppercase tracking-[0.3em] transition hover:bg-[color:var(--primary)]"
              >
                Start your journey →
              </button>
              <a
                href="#services"
                data-testid="intro-explore-services"
                className="link-underline font-mono text-[10px] uppercase tracking-[0.3em] text-[color:var(--muted)]"
              >
                Explore services →
              </a>
            </motion.div>
          </div>

          {/* Right — IAO seal + stats */}
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.1, ease: [0.7, 0, 0.2, 1] }}
              className="relative aspect-square overflow-hidden border border-[color:var(--border)] bg-[color:var(--surface)] p-8 md:p-10"
            >
              <div className="absolute inset-0 spotlight opacity-40" />
              <div className="relative flex h-full flex-col items-center justify-center gap-6 text-center">
                <div className="relative">
                  <div className="absolute -inset-4 rounded-full bg-white/5 blur-2xl" />
                  <img
                    src={LOGOS.iao}
                    alt="IAO Accredited Institution"
                    className="relative h-40 w-40 rounded-full object-cover md:h-52 md:w-52"
                    data-testid="iao-seal"
                  />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[color:var(--muted)]">
                    Accredited by
                  </div>
                  <div className="mt-2 font-serif text-2xl leading-tight md:text-3xl">
                    International{" "}
                    <span className="italic-serif text-[color:var(--primary)]">
                      Accreditation Org.
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* stats strip */}
            <div className="mt-3 grid grid-cols-2 gap-3">
              {stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: 0.1 + i * 0.08 }}
                  className="border border-[color:var(--border)] bg-[color:var(--surface)] p-5"
                  data-testid={`intro-stat-${i}`}
                >
                  <div className="font-serif text-3xl leading-none text-[color:var(--primary)] md:text-4xl">
                    {s.n}
                  </div>
                  <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.25em] text-[color:var(--muted)]">
                    {s.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Intro;
